<!--
 * @Description: 人员管理
 * @Date: 2024-03-15 11:13:47
-->
<template>
  <div v-xrk-loading="loading" style="background-color: #fff; height: 100%">
    <XrkTable
      ref="TableRef"
      :request-now="false"
      :search-config="searchConfig"
      :table-config="tableConfig"
      :get-list-ajax="getList"
    ></XrkTable>
  </div>
</template>

<script lang="tsx" setup>
import {
  XrkTable,
  XrkTableProps,
  BaseButton,
  XrkTableRef,
  createDialog,
  useInputColumn,
  createDisabledDate,
  XrkMessage,
  BaseTextContent
} from 'xrk-components';
import { dealTableColumnstextLineClamp } from '../tools/index';
import { Person, TabaleColumnType } from '../data-source/types';
import { nextTick, ref } from 'vue';
import { createTableConfig } from '../components/table-columns-set';
import { Setting } from '@element-plus/icons-vue';
import { ROAD_TYPE, ROAD_TYPE_MAP } from '../config/index';
import {
  getPeopleList,
  getPeopleProductList,
  getTableColumnList,
  getPeopleEnterpriseList,
  peopleExport,
  peopleExportAll
} from '../data-source/api';
import { useZsStore } from '../store';
import { useRoute } from 'vue-router';

type TableConfig = XrkTableProps<
  Person['search'],
  Person['row'] & { _insert?: '' }
>;

const route = useRoute();
const { isOssRole, isCsoRole, enterpriseId, setValue } = useZsStore();
setValue('enterpriseId', route.query.enterpriseId as any);
const loading = ref(true);
const useEnterpriseSelect = isOssRole.value && !enterpriseId.value;

const { TableRef } = (() => {
  const TableRef = ref<XrkTableRef>();
  const res = {
    TableRef,
    initList: () => TableRef.value?.initList(),
    initCurrentList: () => TableRef.value?.initCurrentList()
  };
  return res;
})();

const dealSearchData = (params: Person['search']) => {
  const labelList = Object.keys(params).reduce((pre, cur) => {
    if (cur.indexOf('_') > -1) {
      pre.push({ labelName: cur, labelValue: params[cur] || null });
    }
    return pre;
  }, []);
  return {
    enterpriseId: enterpriseId.value,
    ...params,
    platform: isOssRole.value ? 3 : isCsoRole.value ? 2 : 1,
    labelList: labelList || [],
    applyDateStart: params.applyDate?.[0],
    applyDateEnd: params.applyDate?.[1],
    updateDateStart: params.updateDate?.[0],
    updateDateEnd: params.updateDate?.[1]
  } as Person['search'];
};

const getList: TableConfig['getListAjax'] = async params => {
  if (useEnterpriseSelect && !params['enterpriseId']) return;
  return await getPeopleList(dealSearchData(params));
};

const { searchConfig, tableConfig, setTableColumns } = (() => {
  const previewPersonDetail = (row: Person['row']) => {
    const list = [
      { 姓名: [row['fullName']] },
      { 手机号: [row['phone']] },
      { 申请途径: [ROAD_TYPE_MAP[row['source']]] },
      { 申请产品: [row['name'] || row['productName']] },
      ...(row.labelDataDetailList || [])
    ];
    if (isOssRole.value) {
      list.unshift(
        { 企业名称: [row['enterpriseName']] },
        { 企业手机号: [row['enterprisePhone']] }
      );
    }
    createDialog({
      title: '详情',
      width: 800,
      showFooter: false,
      showClose: true,
      contentRender: () => (
        <BaseTextContent
          style={{
            display: 'grid',
            'grid-template-columns': 'repeat(2, 1fr)',
            gap: '0 16px'
          }}
        >
          {list?.map(item => {
            // item是个对象，后端（仕雄）说一定只会有一条
            return (
              <div
                style={{
                  marginBottom: '12px'
                }}
              >
                <p
                  style={{
                    color: '#333',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '4px'
                  }}
                >
                  {Object.keys(item).join('、')}
                </p>
                <p
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #DDDDDD',
                    padding: '8px',
                    wordBreak: 'break-word'
                  }}
                >
                  {Object.values(item)[0]?.join('、')}
                </p>
              </div>
            );
          })}
        </BaseTextContent>
      )
    });
  };
  const initTableColumns = (() => {
    let base: { [key: string]: Array<any> } = {};
    let _enterpriseId: any = enterpriseId.value;
    const fn: () => Promise<
      Array<TabaleColumnType<keyof Person['search'] | keyof Person['row']>>
    > = async () => {
      let ___enterpriseId: any = enterpriseId.value || 'all';
      if (!_enterpriseId && useEnterpriseSelect) {
        const { data } = await getPeopleEnterpriseList();
        if (!Array.isArray(data) || data.length === 0) {
          return [];
        }
        _enterpriseId = data[0]?.value;
        TableRef.value.setSearchValue('enterpriseId', _enterpriseId);
      }
      if (useEnterpriseSelect) {
        ___enterpriseId = TableRef.value.getSearchValue()?.enterpriseId;
      }
      if ((base[___enterpriseId] || []).length === 0) {
        const { data } = await getTableColumnList({
          enterpriseId: ___enterpriseId === 'all' ? void 0 : ___enterpriseId
        });
        let chooseCount = 0;
        base[___enterpriseId] = data.map((item, index) => {
          if (item.isFix == 1 || chooseCount <= 10) {
            chooseCount++;
          }
          return {
            key: item.name,
            label: item.nameCh,
            isSearch: item.isFix == 1,
            isShow: item.isFix == 1 || chooseCount <= 10,
            disabled: item.isFix == 1,
            id: index + 1,
            sort: index + 1
          };
        });
      }
      return base[___enterpriseId];
    };
    return fn;
  })();
  const res = createTableConfig<TableConfig>({
    serachConfig: {
      searchColumns: useEnterpriseSelect
        ? [
            {
              type: 'select',
              prop: 'enterpriseId',
              label: '企业名称',
              disabledReset: true,
              config: {
                init: true,
                filterUseRemote: true,
                remoteMethod: (searchVal: string) =>
                  getPeopleEnterpriseList({ productName: searchVal || '' })
              },
              listener: {
                onChange: () => {
                  res.setTable();
                }
              }
            }
          ]
        : [],
      otherComponents: [
        ...(() => {
          const res = [
            {
              render: () => (
                <BaseButton
                  type="primary2"
                  click={async () => {
                    if (
                      useEnterpriseSelect &&
                      !TableRef.value.getSearchValue()?.enterpriseId
                    ) {
                      XrkMessage.error('至少选择一个企业');
                      return;
                    }
                    await peopleExport(
                      dealSearchData(TableRef.value.getSearchValue())
                    );
                  }}
                >
                  导出
                </BaseButton>
              )
            }
          ];
          if (useEnterpriseSelect) {
            res.push({
              render: () => (
                <BaseButton
                  type="primary2"
                  click={async () => {
                    await peopleExportAll({});
                  }}
                >
                  导出全部
                </BaseButton>
              )
            });
          }
          return res;
        })(),
        {
          fixed: 'right',
          render: () => (
            <BaseButton
              link
              icon={Setting}
              onClick={setTableColumns}
              type="default"
            >
              设置字段
            </BaseButton>
          )
        }
      ]
    },
    tableConifg: {
      tableColumns: dealTableColumnstextLineClamp<
        TableConfig['tableConfig']['tableColumns']
      >([
        { type: 'index', label: '序号', width: 60 },
        { prop: '_insert', label: '' },
        {
          width: 100,
          label: '操作',
          render: ({ row }) => {
            return (
              <>
                <BaseButton
                  type="text"
                  onClick={() => previewPersonDetail(row)}
                >
                  详情
                </BaseButton>
              </>
            );
          }
        }
      ])
    },
    initTableColumns: initTableColumns,
    tableDefaultColumnConfig: {
      source: {
        width: 100,
        prop: 'source',
        label: '申请途径',
        formatter: ({ cellValue }) => ROAD_TYPE_MAP[cellValue as any]
      },
      fullName: {
        prop: 'fullName',
        label: '姓名',
        render: ({ cellValue, row }) => (
          <div
            style={{ color: 'var(--xrk-color-primary2)', cursor: 'pointer' }}
            onClick={() => previewPersonDetail(row)}
          >
            {cellValue}
          </div>
        )
      }
    },
    searchDefaultConfig: {
      applyDt: {
        type: 'datePicker',
        prop: 'applyDate',
        label: '申请时间',
        config: {
          type: 'daterange',
          startPlaceholder: '开始时间',
          endPlaceholder: '结束时间',
          disabledDate: createDisabledDate({ year: 1, month: 0, day: 0 })
        }
      },
      updateDt: {
        type: 'datePicker',
        prop: 'updateDate',
        label: '更新时间',
        config: {
          type: 'daterange',
          startPlaceholder: '开始时间',
          endPlaceholder: '结束时间',
          disabledDate: createDisabledDate({ year: 1, month: 0, day: 0 })
        }
      },
      fullName: useInputColumn({
        prop: 'fullName',
        label: '姓名',
        config: { maxlength: 20, clearable: true }
      }),
      phone: useInputColumn({
        prop: 'phone',
        label: '手机号',
        style: { width: '160px' },
        config: {
          type: 'tel',
          maxlength: 11,
          clearable: true
        }
      }),
      source: {
        type: 'select',
        prop: 'source',
        label: '申请途径',
        config: {
          clearable: true,
          options: ROAD_TYPE
        }
      },
      productName: {
        type: 'select',
        prop: 'productId',
        label: '申请产品',
        config: {
          filterUseRemote: true,
          remoteMethod: (searchVal: string) =>
            getPeopleProductList({
              productName: searchVal,
              enterpriseId:
                TableRef.value.getSearchValue()?.enterpriseId ||
                enterpriseId.value
            }),
          clearable: true
        }
      }
    },
    getLocalStorageKey: () =>
      `${
        enterpriseId.value ||
        TableRef.value.getSearchValue()?.enterpriseId ||
        'all'
      }_zs-v3-table-custom-columns`,
    callback: changeKeys => {
      nextTick(async () => {
        changeKeys.forEach(key => TableRef.value.setSearchValue(key, void 0));
        await TableRef.value.initList();
        loading.value = false;
      });
    },
    excludeSearchKeys: isOssRole.value
      ? ['enterprisePhone', 'enterpriseName']
      : []
  });
  return res;
})();
</script>

<style></style>
